import {HelpContent, HelpCategory, HelpSections, HelpTopic, HelpSection} from "../types/help.types";
import {strings} from "../constants/strings";

export const helpContent: HelpContent = {
  // Graphics & Performance
  profile: {
    id: "profile",
    title: "Graphics Profile",
    content: strings.profile,
    category: HelpCategory.GRAPHICS,
    keywords: ["graphics", "quality", "polygon", "GPU", "profile"]
  },
  resolution: {
    id: "resolution",
    title: "Resolution",
    content: strings.resolution,
    category: HelpCategory.GRAPHICS,
    relatedTopics: ["profile"],
    keywords: ["resolution", "size", "image", "settings"]
  },
  fps: {
    id: "fps",
    title: "FPS (Frames Per Second)",
    content: strings.fps,
    category: HelpCategory.GRAPHICS,
    relatedTopics: ["profile", "resolution"],
    keywords: ["fps", "frames", "smooth", "performance"]
  },
  percentile: {
    id: "percentile",
    title: "1st Percentile",
    content: strings.percentile,
    category: HelpCategory.GRAPHICS,
    keywords: ["percentile", "statistics", "performance"]
  },
  stdev: {
    id: "stdev",
    title: "Standard Deviation",
    content: strings.stdev,
    category: HelpCategory.GRAPHICS,
    keywords: ["standard deviation", "variation", "fps"]
  },

  // UDP Network
  udpNetworkPackets: {
    id: "udpNetworkPackets",
    title: "UDP Network Packets",
    content: strings.udpNetworkPackets,
    category: HelpCategory.NETWORK_UDP,
    keywords: ["UDP", "network", "packets", "traffic", "crowding"]
  },
  udpMaxStc: {
    id: "udpMaxStc",
    title: "UDP Max StC",
    content: strings.udpMaxStc,
    category: HelpCategory.NETWORK_UDP,
    relatedTopics: ["udpAvgStc"],
    keywords: ["UDP", "server", "client", "max", "packets"]
  },
  udpAvgStc: {
    id: "udpAvgStc",
    title: "UDP Avg StC",
    content: strings.udpAvgStc,
    category: HelpCategory.NETWORK_UDP,
    relatedTopics: ["udpMaxStc"],
    keywords: ["UDP", "server", "client", "average", "packets"]
  },
  udpMaxCtS: {
    id: "udpMaxCtS",
    title: "UDP Max CtS",
    content: strings.udpMaxCtS,
    category: HelpCategory.NETWORK_UDP,
    relatedTopics: ["udpAvgCtS"],
    keywords: ["UDP", "client", "server", "max", "packets"]
  },
  udpAvgCtS: {
    id: "udpAvgCtS",
    title: "UDP Avg CtS",
    content: strings.udpAvgCtS,
    category: HelpCategory.NETWORK_UDP,
    relatedTopics: ["udpMaxCtS"],
    keywords: ["UDP", "client", "server", "average", "packets"]
  },
  udpRxErr: {
    id: "udpRxErr",
    title: "UDP Rx Errors",
    content: strings.udpRxErr,
    category: HelpCategory.NETWORK_UDP,
    relatedTopics: ["udpTxErr"],
    keywords: ["UDP", "receive", "errors", "packets"]
  },
  udpTxErr: {
    id: "udpTxErr",
    title: "UDP Tx Errors",
    content: strings.udpTxErr,
    category: HelpCategory.NETWORK_UDP,
    relatedTopics: ["udpRxErr"],
    keywords: ["UDP", "transmit", "errors", "packets"]
  },
  udpCrowdedness: {
    id: "udpCrowdedness",
    title: "UDP Crowdedness",
    content: strings.udpCrowdedness,
    category: HelpCategory.NETWORK_UDP,
    relatedTopics: ["udpCrowdednessTier"],
    keywords: ["UDP", "crowding", "group", "size"]
  },
  udpCrowdednessTier: {
    id: "udpCrowdednessTier",
    title: "UDP Crowdedness Tier",
    content: strings.udpCrowdednessTier,
    category: HelpCategory.NETWORK_UDP,
    relatedTopics: ["udpCrowdedness"],
    keywords: ["UDP", "crowding", "tier", "label"]
  },
  networkTimeoutsUdp: {
    id: "networkTimeoutsUdp",
    title: "UDP Network Timeouts",
    content: strings.networkTimeoutsUdp,
    category: HelpCategory.NETWORK_UDP,
    keywords: ["UDP", "timeout", "network", "connection"]
  },
  networkErrorsUdp: {
    id: "networkErrorsUdp",
    title: "UDP Network Errors",
    content: strings.networkErrorsUdp,
    category: HelpCategory.NETWORK_UDP,
    keywords: ["UDP", "errors", "network", "transmission"]
  },
  udpNetworkConnectionAttempts: {
    id: "udpNetworkConnectionAttempts",
    title: "UDP Network Connection Attempts",
    content: strings.udpNetworkConnectionAttempts,
    category: HelpCategory.NETWORK_UDP,
    keywords: ["UDP", "connection", "attempts", "network"]
  },
  udpNetworkDisconnects: {
    id: "udpNetworkDisconnects",
    title: "UDP Network Disconnects",
    content: strings.udpNetworkDisconnects,
    category: HelpCategory.NETWORK_UDP,
    keywords: ["UDP", "disconnect", "network", "connection"]
  },

  // TCP Network
  networkErrorsTcp: {
    id: "networkErrorsTcp",
    title: "TCP Network Errors",
    content: strings.networkErrorsTcp,
    category: HelpCategory.NETWORK_TCP,
    keywords: ["TCP", "errors", "network", "API"]
  },
  tcpNetworkDisconnects: {
    id: "tcpNetworkDisconnects",
    title: "TCP Network Disconnects",
    content: strings.tcpNetworkDisconnects,
    category: HelpCategory.NETWORK_TCP,
    keywords: ["TCP", "disconnect", "network", "connection"]
  },
  networkTimeoutsTcp: {
    id: "networkTimeoutsTcp",
    title: "TCP Network Timeouts",
    content: strings.networkTimeoutsTcp,
    category: HelpCategory.NETWORK_TCP,
    keywords: ["TCP", "timeout", "network", "connection"]
  },

  // ANT+
  rxfails: {
    id: "rxfails",
    title: "ANT+ RxFailures",
    content: strings.rxfails,
    category: HelpCategory.ANT_PLUS,
    relatedTopics: ["antDisconnects"],
    keywords: ["ANT+", "interference", "RF", "WiFi", "dropout"]
  },
  antDisconnects: {
    id: "antDisconnects",
    title: "ANT+ Disconnects",
    content: strings.antDisconnects,
    category: HelpCategory.ANT_PLUS,
    relatedTopics: ["rxfails"],
    keywords: ["ANT+", "disconnect", "dropout", "sensor"]
  },
  antFetTransferFailures: {
    id: "antFetTransferFailures",
    title: "ANT+ FET Transfer Failures",
    content: strings.antFetTransferFailures,
    category: HelpCategory.ANT_PLUS,
    keywords: ["ANT+", "FET", "trainer", "gradient", "resistance"]
  },

  // Bluetooth
  bluetoothDisconnects: {
    id: "bluetoothDisconnects",
    title: "Bluetooth Disconnects",
    content: strings.bluetoothDisconnects,
    category: HelpCategory.BLUETOOTH,
    keywords: ["Bluetooth", "disconnect", "dropout", "BLE"]
  },
  bluetoothFtmsResends: {
    id: "bluetoothFtmsResends",
    title: "Bluetooth FTMS Resends",
    content: strings.bluetoothFtmsResends,
    category: HelpCategory.BLUETOOTH,
    relatedTopics: ["bluetoothTrainerResets"],
    keywords: ["Bluetooth", "FTMS", "resend", "trainer"]
  },
  bluetoothTrainerResets: {
    id: "bluetoothTrainerResets",
    title: "Bluetooth Trainer Resets",
    content: strings.bluetoothTrainerResets,
    category: HelpCategory.BLUETOOTH,
    relatedTopics: ["bluetoothFtmsResends"],
    keywords: ["Bluetooth", "trainer", "reset", "FTMS"]
  },

  // Search
  searchBasics: {
    id: "searchBasics",
    title: "Search Basics",
    content: strings.searchBasics,
    category: HelpCategory.SEARCH,
    keywords: ["search", "basic", "hardware", "compatibility", "benchmark"]
  },
  searchBooleanOperators: {
    id: "searchBooleanOperators",
    title: "Boolean Operators (AND, OR, NOT)",
    content: strings.searchBooleanOperators,
    category: HelpCategory.SEARCH,
    relatedTopics: ["searchSymbols"],
    keywords: ["boolean", "AND", "OR", "NOT", "operators", "logic"]
  },
  searchSymbols: {
    id: "searchSymbols",
    title: "Search Symbols (!, +, -)",
    content: strings.searchSymbols,
    category: HelpCategory.SEARCH,
    relatedTopics: ["searchBooleanOperators"],
    keywords: ["symbols", "exclamation", "plus", "minus", "operators"]
  },
  searchPhrases: {
    id: "searchPhrases",
    title: "Exact Phrase Search",
    content: strings.searchPhrases,
    category: HelpCategory.SEARCH,
    keywords: ["phrases", "quotes", "exact", "model", "numbers"]
  },
  searchAdvanced: {
    id: "searchAdvanced",
    title: "Advanced Search Techniques",
    content: strings.searchAdvanced,
    category: HelpCategory.SEARCH,
    relatedTopics: ["searchBooleanOperators", "searchPhrases"],
    keywords: ["advanced", "grouping", "proximity", "wildcards", "parentheses"]
  },
  searchWindowsArm: {
    id: "searchWindowsArm",
    title: "Finding Windows ARM Devices",
    content: strings.searchWindowsArm,
    category: HelpCategory.SEARCH,
    keywords: ["Windows", "ARM", "Snapdragon", "Adreno", "D3D12", "D3D11"]
  },
  searchCrowding: {
    id: "searchCrowding",
    title: "Understanding Crowdedness",
    content: strings.searchCrowding,
    category: HelpCategory.SEARCH,
    relatedTopics: ["udpCrowdedness", "udpCrowdednessTier"],
    keywords: ["crowding", "solo", "company", "packed", "fondo", "performance"]
  },

  // General
  latency: {
    id: "latency",
    title: "Network Latency",
    content: strings.latency,
    category: HelpCategory.GENERAL,
    keywords: ["latency", "delay", "network", "ping"]
  },
  sessionDuration: {
    id: "sessionDuration",
    title: "Session Duration",
    content: "The total time from start to end of the log file.",
    category: HelpCategory.GENERAL,
    keywords: ["duration", "time", "session", "length"]
  },
  activitiesCount: {
    id: "activitiesCount",
    title: "Activities Count",
    content:
      "The number of activities (rides or runs) in the log file. When more than one activity exists, click on the activity name below to select a different activity.",
    category: HelpCategory.GENERAL,
    keywords: ["activities", "rides", "runs", "count"]
  },
  loadTime: {
    id: "loadTime",
    title: "Load Time",
    content: "Time elapsed from start of the log file to the device pairing screen appearing.",
    category: HelpCategory.GENERAL,
    keywords: ["load", "startup", "pairing", "time"]
  },
  gameVersion: {
    id: "gameVersion",
    title: "Game Version",
    content: "The version of the game client used.",
    category: HelpCategory.GENERAL,
    keywords: ["game", "version", "client"]
  },
  launcherVersion: {
    id: "launcherVersion",
    title: "Launcher Version",
    content: "The version of the application used to start up the game client.",
    category: HelpCategory.GENERAL,
    keywords: ["launcher", "version", "startup", "application"]
  },
  videoScreenshots: {
    id: "videoScreenshots",
    title: "Video Screenshots",
    content: "Disable Video Screenshots for best FPS performance.",
    category: HelpCategory.GRAPHICS,
    relatedTopics: ["fps"],
    keywords: ["video", "screenshots", "performance", "fps"]
  },
  gpuProfileMatch: {
    id: "gpuProfileMatch",
    title: "GPU Profile Match",
    content:
      "Indicates whether the game engine has a profile match for this GPU. A match means the game engine can optimize graphics settings for your specific GPU.",
    category: HelpCategory.GRAPHICS,
    relatedTopics: ["profile"],
    keywords: ["gpu", "profile", "match", "graphics", "optimization"]
  },
  worldClockOffsets: {
    id: "worldClockOffsets",
    title: "World Clock Offsets",
    content: strings.worldClockOffsets,
    category: HelpCategory.GENERAL,
    keywords: ["clock", "sync", "time", "offset"]
  },
  companionAppConnectionAttempts: {
    id: "companionAppConnectionAttempts",
    title: "Companion App Connection Attempts",
    content: strings.companionAppConnectionAttempts,
    category: HelpCategory.GENERAL,
    keywords: ["companion", "app", "mobile", "connection"]
  },
  pingTestLatency: {
    id: "pingTestLatency",
    title: "Ping Test - Understanding Latency",
    content: strings.pingTestLatency,
    category: HelpCategory.GENERAL,
    relatedTopics: ["latency"],
    keywords: ["ping", "latency", "network", "connection", "test", "delay"]
  }
};

export const helpSections: HelpSections = {
  [HelpCategory.GRAPHICS]: {
    category: HelpCategory.GRAPHICS,
    title: "Graphics & Performance",
    description: "Understanding frame rates, graphics profiles, and visual performance metrics",
    topics: ["profile", "resolution", "fps", "percentile", "stdev", "videoScreenshots", "gpuProfileMatch"]
  },
  [HelpCategory.NETWORK_UDP]: {
    category: HelpCategory.NETWORK_UDP,
    title: "UDP Network",
    description: "Real-time game data communication between your client and the server",
    topics: [
      "udpNetworkPackets",
      "udpMaxStc",
      "udpAvgStc",
      "udpMaxCtS",
      "udpAvgCtS",
      "udpRxErr",
      "udpTxErr",
      "udpCrowdedness",
      "udpCrowdednessTier",
      "networkTimeoutsUdp",
      "networkErrorsUdp",
      "udpNetworkConnectionAttempts",
      "udpNetworkDisconnects"
    ]
  },
  [HelpCategory.NETWORK_TCP]: {
    category: HelpCategory.NETWORK_TCP,
    title: "TCP Network",
    description: "Reliable data transfer for logins, uploads, and API requests",
    topics: ["networkErrorsTcp", "tcpNetworkDisconnects", "networkTimeoutsTcp"]
  },
  [HelpCategory.ANT_PLUS]: {
    category: HelpCategory.ANT_PLUS,
    title: "ANT+ Sensors",
    description: "ANT+ sensor communication, interference, and dropout issues",
    topics: ["rxfails", "antDisconnects", "antFetTransferFailures"]
  },
  [HelpCategory.BLUETOOTH]: {
    category: HelpCategory.BLUETOOTH,
    title: "Bluetooth Sensors",
    description: "Bluetooth sensor communication and trainer control",
    topics: ["bluetoothDisconnects", "bluetoothFtmsResends", "bluetoothTrainerResets"]
  },
  [HelpCategory.SEARCH]: {
    category: HelpCategory.SEARCH,
    title: "Search Guide",
    description: "Master TranerDX's powerful search to find hardware compatibility and performance data",
    topics: [
      "searchBasics",
      "searchBooleanOperators",
      "searchSymbols",
      "searchPhrases",
      "searchAdvanced",
      "searchWindowsArm",
      "searchCrowding"
    ]
  },
  [HelpCategory.GENERAL]: {
    category: HelpCategory.GENERAL,
    title: "General",
    description: "Other metrics and system information",
    topics: [
      "latency",
      "pingTestLatency",
      "worldClockOffsets",
      "companionAppConnectionAttempts",
      "sessionDuration",
      "activitiesCount",
      "loadTime",
      "gameVersion",
      "launcherVersion"
    ]
  }
};

export function getHelpTopic(topicId: string): HelpTopic | undefined {
  return helpContent[topicId];
}

export function getHelpSection(category: HelpCategory): HelpSection | undefined {
  return helpSections[category];
}

export function searchHelpTopics(query: string): HelpTopic[] {
  const lowerQuery = query.toLowerCase();
  return Object.values(helpContent).filter(
    (topic) =>
      topic.title.toLowerCase().includes(lowerQuery) ||
      topic.content.toLowerCase().includes(lowerQuery) ||
      topic.keywords?.some((keyword) => keyword.toLowerCase().includes(lowerQuery))
  );
}
